import React, { useCallback, useEffect, useState } from 'react';
import { engagementApi, leadApi, linkedInAdsApi } from '../services/api';
import { BySource, LeadRow, LinkedInCampaign, LinkedInReport, SourceFunnel } from '../types';
import { fmtDayTime, fmtINR, istToday } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import StageChips, { Dash } from '../components/StageChips';
import {
  Section, Empty, Failed, Loading, Note, Out, PageFrame, picked, Pill, PlatformLeadsCard, Strip, Th, VisitsCell,
  btn, btnTone, td, tdBig, tdOurs, tr, trClick,
} from '../components/PlatformBits';

/** One LinkedIn campaign: what LinkedIn delivered next to what the CRM holds
 *  for the people it sent. LinkedIn tags every click with utm_id=<campaign
 *  id>, so a lead's first-touch tag carries the campaign in its campaign
 *  slot — that is the join. Clicks from before the site read utm_id arrive
 *  with no campaign; while exactly one campaign is running they belong to
 *  it, and the row says so. */
interface CampaignRow {
  key: string;
  campaign: LinkedInCampaign | null;   // null = LinkedIn arrivals that match no campaign
  tags: string[];
  untagged: boolean;                   // includes the no-campaign tag
  landed: number;
  tapped: number;
  new_leads: number;
  stages: Record<string, number>;
  visits_scheduled: number;
  visits_conducted: number;
}

const UNMATCHED = 'unmatched';
const LI_ADS = 'https://www.linkedin.com/campaignmanager/accounts';

const rollUp = (campaigns: LinkedInCampaign[], sources: SourceFunnel[]): CampaignRow[] => {
  const blank = (key: string, campaign: LinkedInCampaign | null): CampaignRow =>
    ({ key, campaign, tags: [], untagged: false, landed: 0, tapped: 0, new_leads: 0, stages: {}, visits_scheduled: 0, visits_conducted: 0 });
  const rows = new Map<string, CampaignRow>(campaigns.map((c) => [c.campaign_id, blank(c.campaign_id, c)]));
  const active = campaigns.filter((c) => c.status === 'ACTIVE');
  sources.filter((s) => s.tag && s.source === 'linkedin').forEach((s) => {
    const byId = s.campaign ? campaigns.find((c) => c.campaign_id === s.campaign) : undefined;
    const home = byId ?? (!s.campaign && active.length === 1 ? active[0] : undefined);
    const key = home?.campaign_id ?? UNMATCHED;
    if (!rows.has(key)) rows.set(key, blank(key, null));
    const r = rows.get(key)!;
    r.tags.push(s.tag!);
    if (!s.campaign) r.untagged = true;
    r.landed += s.opened;
    r.tapped += s.cta_clicked;
    r.new_leads += s.new_leads;
    r.visits_scheduled += s.visits_scheduled;
    r.visits_conducted += s.visits_conducted;
    Object.entries(s.stages).forEach(([st, n]) => { r.stages[st] = (r.stages[st] ?? 0) + n; });
  });
  return Array.from(rows.values()).sort((a, b) => (b.campaign?.cost ?? -1) - (a.campaign?.cost ?? -1));
};

/** The selected row's first cell carries a forest rule on its left edge. */

const n = (v: number) => v.toLocaleString('en-IN');

const LinkedInPage: React.FC = () => {
  const [range, setRange] = useState({ from: istToday(), to: istToday() });
  const [report, setReport] = useState<LinkedInReport | null>(null);
  const [data, setData] = useState<BySource | null>(null);
  const [leads, setLeads] = useState<LeadRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setReport(null); setData(null); setLeads(null); setError(null);
    Promise.all([
      linkedInAdsApi.report(range.from, range.to),
      engagementApi.bySource(range.from, range.to),
    ]).then(([rep, bs]) => {
      setReport(rep); setData(bs);
      const tags = rollUp(rep.campaigns, bs.sources).flatMap((r) => r.tags);
      return leadApi.byFirstTouch(tags, range.from, range.to).then(setLeads);
    }).catch((err: any) => setError(err?.response?.data?.detail || err.message));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    setRefreshing(true);
    linkedInAdsApi.refresh()
      .then(load)
      .catch((err: any) => setError(`Refresh failed: ${err?.response?.data?.detail || err.message}`))
      .finally(() => setRefreshing(false));
  };

  if (error) return <Failed what="LinkedIn" error={error} />;

  const rows = report && data ? rollUp(report.campaigns, data.sources) : [];
  if (selected && !rows.some((r) => r.key === selected)) setSelected(null);
  const shown = selected ? rows.filter((r) => r.key === selected) : rows;
  const selectedRow = selected ? shown[0] : undefined;
  const tot = shown.reduce((a, r) => ({
    spend: a.spend + (r.campaign?.cost ?? 0), clicks: a.clicks + (r.campaign?.clicks ?? 0),
    landed: a.landed + r.landed, tapped: a.tapped + r.tapped, leads: a.leads + r.new_leads,
    sched: a.sched + r.visits_scheduled, done: a.done + r.visits_conducted,
  }), { spend: 0, clicks: 0, landed: 0, tapped: 0, leads: 0, sched: 0, done: 0 });

  const strip: StripItem[] = [
    { v: tot.done, k: 'site visits', cls: 'visited' },
    { v: tot.sched, k: 'scheduled', cls: 'gold' },
    { v: tot.leads, k: 'new leads' },
    { v: n(tot.landed), k: 'landed on the site' },
    { v: n(tot.clicks), k: 'clicks LinkedIn billed' },
    { v: fmtINR(tot.spend), k: 'spend' },
    ...(tot.leads > 0 ? [{ v: fmtINR(tot.spend / tot.leads), k: 'per lead' }] : []),
  ];

  const selectedTags = new Set(shown.flatMap((r) => r.tags));
  const leadRows = (leads ?? []).filter((l) => !selected || selectedTags.has(l.acquisition?.src ?? ''));
  const tagToRow = new Map(rows.flatMap((r) => r.tags.map((t) => [t, r] as const)));
  const rowName = (r: CampaignRow) => r.campaign?.name ?? 'Link matches no campaign';
  const creatives = (report?.creatives ?? []).filter((c) => !selected || c.campaign_id === selected);
  const account = report?.account_id ?? '';
  const conversionsSince = '2026-09-28';  // the GTM pixels were fixed 2026-09-27 (MAG-102); zeros before are the pixel, not the ads

  return (
    <PageFrame
      title="LinkedIn"
      badge={{ platform: 'linkedin' }}
      subline={<>
        MagicMetrics-Bhuvana · ₹ · {report?.synced_at ? <>synced <b className="text-charcoal">{fmtDayTime(report.synced_at)}</b></> : 'sync time unknown'}
        {account && <> · <Out href={`${LI_ADS}/${account}/campaigns`}>Open Campaign Manager</Out></>}
      </>}
      action={<button onClick={refresh} disabled={refreshing || !report} className={`${btn} ${btnTone.forest}`}>{refreshing ? 'Refreshing…' : 'Refresh from LinkedIn'}</button>}
      bar={<DateRangeBar from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />}
    >
      {!(report && data) && <Loading what="LinkedIn" />}

      {report && data && (
        <>
          <Strip><NumberStrip items={strip} /></Strip>

          <Section title="Campaigns" note={selectedRow
            ? <>leads below are from <b className="text-charcoal">{rowName(selectedRow)}</b> · <button className="font-semibold text-forest hover:underline" onClick={() => setSelected(null)}>show all</button></>
            : 'click a campaign to see its leads'}>
            <table className="w-full min-w-[1100px] border-collapse">
              <thead><tr>
                <Th left>Campaign</Th><Th>Spend</Th><Th>Clicks</Th><Th>Video</Th><Th>LinkedIn conv.</Th>
                <Th ours>Landed</Th><Th ours>Tapped</Th><Th ours>New leads</Th><Th ours>Stage</Th><Th ours>Site visits</Th><Th>₹ / lead</Th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <Empty cols={11}>No LinkedIn spend and no LinkedIn arrivals in this window.</Empty>}
                {rows.map((r) => {
                  const c = r.campaign;
                  const name = rowName(r);
                  return (
                    <tr key={r.key} onClick={() => setSelected(selected === r.key ? null : r.key)}
                        className={trClick(selected === r.key)}>
                      <td className={`px-3 py-3 ${picked(selected === r.key)}`}>
                        <div className={`font-semibold text-[13.5px] leading-snug truncate max-w-[320px] ${c ? '' : 'text-brick'}`} title={name}>{name}</div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap text-[11px] text-graphite">
                          {c && <Pill tone={c.status === 'ACTIVE' ? 'ok' : undefined}>{(c.status ?? '').toLowerCase()}</Pill>}
                          {c && <span>{[c.cost_type, c.daily_budget != null ? `${fmtINR(c.daily_budget)} / day` : null, c.reach ? `reached ${n(c.reach)} in 7 days` : null].filter(Boolean).join(' · ')}</span>}
                          {r.untagged && <Pill tone="gold">includes clicks tagged before 27 Sep</Pill>}
                        </div>
                      </td>
                      <td className={tdBig}>{c ? <>{fmtINR(c.cost)}<div className="text-[11px] text-graphite font-normal">{n(c.impressions)} impressions</div></> : <Dash />}</td>
                      <td className={`${td} text-right`}>{c?.clicks ? <>{n(c.clicks)}<div className="text-[11px] text-graphite">{fmtINR(c.cost / c.clicks)} each</div></> : <Dash />}</td>
                      <td className={`${td} text-right`}>{c?.video_starts ? <>{n(c.video_starts)}<div className="text-[11px] text-graphite">{n(c.video_completions)} to the end</div></> : <Dash />}</td>
                      <td className={`${td} text-right`}>{c?.conversions || <Dash />}</td>
                      <td className={`${td} text-right`}>{r.landed ? n(r.landed) : <Dash />}</td>
                      <td className={`${td} text-right`}>{r.tapped || <Dash />}</td>
                      <td className={tdOurs}>{r.new_leads || <Dash />}</td>
                      <td className="px-3 py-3 text-right">{r.new_leads ? <StageChips stages={r.stages} /> : <Dash />}</td>
                      <VisitsCell done={r.visits_conducted} sched={r.visits_scheduled} />
                      <td className={`${td} text-right`}>{c && r.new_leads ? fmtINR(c.cost / r.new_leads) : <Dash />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Note>
              <b className="text-charcoal">Landed</b> is our count of arrivals carrying LinkedIn's ad click id; it runs above LinkedIn's billed <b className="text-charcoal">clicks</b> because CPM campaigns bill impressions and count only the clicks LinkedIn attributes — the two are not a ratio.{' '}
              <b className="text-charcoal">LinkedIn conv.</b> — its pixel's count; broken until {conversionsSince}, so earlier zeros are the pixel, not the ads.
            </Note>
          </Section>

          <Section title="Creatives" note={selectedRow ? <>in <b className="text-charcoal">{rowName(selectedRow)}</b></> : 'every creative with spend in the window'}>
            <table className="w-full min-w-[900px] border-collapse">
              <thead><tr><Th left>Creative</Th><Th>Spend</Th><Th>Clicks</Th><Th>Watched</Th><Th>To the end</Th><Th>LinkedIn conv.</Th></tr></thead>
              <tbody>
                {creatives.length === 0 && <Empty cols={6}>No creative spent in this window.</Empty>}
                {creatives.map((c) => {
                  const name = c.media_title ?? c.post_text?.split('\n')[0]?.slice(0, 70) ?? c.creative_id;
                  const text = c.post_text?.replace(/\n/g, ' ');
                  return (
                    <tr key={c.creative_id} className={tr}>
                      <td className="px-3 py-3">
                        <div className="font-semibold text-[13.5px] leading-snug truncate max-w-[420px]" title={name}>{name}</div>
                        <div className="flex items-center gap-1.5 mt-1 text-[11px] text-graphite">
                          <Pill tone={c.is_serving ? 'ok' : undefined}>{c.is_serving ? 'serving' : (c.status ?? '').toLowerCase()}</Pill>
                          {text && <span className="truncate max-w-[300px]" title={text}>{text}</span>}
                          {c.post_urn && <Out href={`https://www.linkedin.com/feed/update/${c.post_urn}`}>post</Out>}
                        </div>
                      </td>
                      <td className={tdBig}>{fmtINR(c.cost)}</td>
                      <td className={`${td} text-right`}>{c.clicks ? <>{n(c.clicks)}<div className="text-[11px] text-graphite">{fmtINR(c.cost / c.clicks)} each</div></> : <Dash />}</td>
                      <td className={`${td} text-right`}>{c.video_views ? <>{n(c.video_views)}<div className="text-[11px] text-graphite">of {n(c.video_starts)} starts</div></> : <Dash />}</td>
                      <td className={`${td} text-right`}>{c.video_completions || <Dash />}</td>
                      <td className={`${td} text-right`}>{c.conversions || <Dash />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>

          <PlatformLeadsCard
            title={selectedRow ? `Leads from ${rowName(selectedRow)}` : 'Leads from LinkedIn'}
            leads={leads}
            rows={leadRows}
            empty="No lead in this window carries a LinkedIn first touch."
            origin={selected ? undefined : { header: 'Campaign', of: (l) => { const r = tagToRow.get(l.acquisition?.src ?? ''); const name = r ? rowName(r) : 'Link matches no campaign'; return <span title={name}>{name}</span>; } }}
          />
        </>
      )}
    </PageFrame>
  );
};

export default LinkedInPage;
